import React, { useEffect, useState } from 'react';
import { generateClient } from 'aws-amplify/api';
import {
  Container,
  Header,
  SpaceBetween,
  Box,
  StatusIndicator,
  ColumnLayout,
  Alert,
  KeyValuePairs
} from '@cloudscape-design/components';
import { parseAiRecommendationPercentageScore } from '../../../common/utility';
import { API } from '@aws-amplify/api';
import { GraphQLAuthMode } from '@aws-amplify/api-graphql';


const client = generateClient({
 
});

const SUBSCRIPTION = `
  subscription OnBroadcast($channelId: String!) {
    onBroadcast(channelId: $channelId) {
      id
      content
      timestamp
    }
  }
`;


const RecommendationsSection = ({ recommendations }) => {
  const formatRecommendations = (recs) => {
    return recs.map(rec => ({
      label: rec.point,
      value: (
        <SpaceBetween size="s">
          <div>{rec.analysis}</div>
        </SpaceBetween>
      )
    }));
  };

  return (recommendations.description_recommendations?.length > 0 ||
        recommendations.admin_recommendations?.length > 0) &&
      (
    
    <Container>
      <SpaceBetween size="l">
        {recommendations.description_recommendations?.length > 0 && (
          <>
            <Header variant="h2">AI Recommendations</Header>
            <KeyValuePairs
              items={formatRecommendations(recommendations.description_recommendations)}
              columns={3}
            />
          </>
        )}

        {recommendations.admin_recommendations?.length > 0 && (
          <>
            {/* <Header variant="h2">Administrative Recommendations</Header> */}
            <KeyValuePairs
              items={formatRecommendations(recommendations.admin_recommendations)}
              columns={3}
            />
          </>
        )}
      </SpaceBetween>
    </Container>
  );
};

const BroadcastSubscription = ({ 
  topicId, 
  initialScore = 0, 
  initialRecommendations = [] 
}) => {
  const [loadingStatus, setLoadingStatus] = useState('');
  const [score, setScore] = useState(initialScore);
  const [recommendations, setRecommendations] = useState(initialRecommendations);
  const [subscription, setSubscription] = useState(null);
  const [loadingText, setLoadingText] = useState('');
  const [error, setError] = useState(null);
  const token = localStorage.getItem('access_token');
  
  // Decode JWT token to get current user ID (not from localStorage which can be stale)
  const getUserIdFromToken = () => {
    if (!token) return null;
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.user_id;
    } catch (e) {
      console.error('Failed to decode token:', e);
      return null;
    }
  };
  
  const userId = getUserIdFromToken();
  console.log('🔑 Decoded userId from token:', userId);
  console.log('🔑 Token payload:', token ? JSON.parse(atob(token.split('.')[1])) : null);
  
  const appendText = (newText) => {
    setLoadingText(prevText => prevText + newText);
  };

  const resetState = () => {
    setLoadingStatus('');
    setScore(0);
    setRecommendations([]);
    setLoadingText('');
    setError(null);
  };

  useEffect(() => {
    console.log('Setting up subscription for:', { userId, topicId, hasToken: !!token });
    
    const sub = client.graphql({
      query: SUBSCRIPTION,
      variables: {
        channelId: String(userId),
      },
      authMode: 'lambda',
      authToken: token
    }).subscribe({
      next: ({ data }) => {
        console.log('Received broadcast message:', data);
        const message = data.onBroadcast;
        const content = JSON.parse(message.content);
        console.log('Parsed content:', content);

        if (content.fanout_topic_id !== topicId) {
          console.log('Topic ID mismatch:', { received: content.fanout_topic_id, expected: topicId });
          return;
        }
        // Handle different actions
        switch (content.action) {
          case 'START':
            resetState();
            return;
          case 'GENERAL_ERROR':
            resetState();
            setError('An error occurred in the AI processing engine. Please try again later.');
            return;
        }

        // Handle loading status updates
        if (content.loading_status) {
          setLoadingStatus(content.loading_status);
        }

        // Handle score updates
        if (content.score) {
          setScore(parseAiRecommendationPercentageScore(content.score));
        }

        if (content.loading_text) {
          appendText(content.loading_text);
        }
        if (content.recommendations) {
          setRecommendations(content.recommendations);
        }

        // if (content.recommendations) {
        //   setRecommendations(content.recommendations);
        // }
        
      },
      error: error => {
        console.error('❌ Subscription error:', error);
        console.error('Error type:', error.constructor.name);
        console.error('Error message:', error.message);
        console.error('Full error:', JSON.stringify(error, null, 2));
      }
    });

    setSubscription(sub);

    // Cleanup subscription on unmount
    return () => {
      if (sub) {
        sub.unsubscribe();
      }
    };
  }, [topicId, userId, token]);

  // Update score and recommendations when initial values change
  useEffect(() => {
    setScore(initialScore);
    setRecommendations(initialRecommendations);
  }, [initialScore, initialRecommendations]);

  if (error) {
    
    return(     
    <div className="p-4 max-w-lg mx-auto">
    <h2 className="text-xl font-bold mb-4">Eduscribe AI Response</h2>
    
    <Alert type="error">{error}</Alert>
    </div>
    )
  }

  return (
    <div className="p-4 max-w-lg mx-auto">
      <h2 className="text-xl font-bold mb-4">Eduscribe AI Response</h2>
      
      {/* Score Display */}
      <div className="mb-4 p-4 bg-blue-50 rounded-lg">
        <h1 className="font-semibold mb-2">
          Readiness Score: {parseAiRecommendationPercentageScore(score)}%
        </h1>
        <p> 
        Disclaimer: This result is generated by artificial intelligence and is provided for guidance purposes only to assist institutions in checking the completeness and clarity of their applications. After the submission, the application will be evaluated and judged by the Institutional Listing Evaluation Panel. 
        </p>
      </div>

      {/* Loading Text Display */}
      {loadingText && (
        <div 
          className="mb-4 p-4 bg-white rounded-lg border border-gray-200 h-64 overflow-y-auto whitespace-pre-wrap"
        >
          {loadingText}
        </div>
      )}

      {/* Loading Status */}
      {loadingStatus && (
        <div className="mb-4 p-3 bg-gray-50 rounded-lg">
          <p className="text-gray-600">
            ◌ {loadingStatus}
          </p>
        </div>
      )}
  
      <RecommendationsSection recommendations={recommendations} />

      {/* Empty State */}
      {!loadingStatus && 
      !recommendations.description_recommendations?.length > 0 &&
      !recommendations.admin_recommendations?.length > 0 &&      
      (
        <p className="text-gray-500 text-center py-4">
          Waiting for analysis to begin...
        </p>
      )}
    </div>
  );
};

export default BroadcastSubscription;
